import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { AgentEvalsService } from './agent-evals.service';
import { AgentEvalRun, AgentEvalTest, AgentEvalTestResult } from './agent-evals.types';
import { AddAgentEvalTestDialogComponent } from './add-test-dialog/add-test-dialog.component';
import { GenerateAgentEvalTestsDialogComponent } from './generate-tests-dialog/generate-tests-dialog.component';
import { RunAgentEvalsDialogComponent } from './run-evals-dialog/run-evals-dialog.component';
import {
  AgentEvalSuccessRules,
  AgentEvalSuccessRulesDialogComponent,
} from './success-rules-dialog/success-rules-dialog.component';
import { AgentEvalTestResultDialogComponent } from './test-result-dialog/test-result-dialog.component';
import { AgentEvalReportDialogComponent } from './report-dialog/report-dialog.component';
import { openPresignedDownload } from 'src/@core/utils/presigned-download.util';

const DEFAULT_RULES: AgentEvalSuccessRules = {
  passMark: 80,
  alertBelow: 75,
  criticalDouble: true,
  notifyBy: 'Email',
};

@Component({
  selector: 'hour4u-admin-agent-evals',
  templateUrl: './agent-evals.component.html',
  styleUrls: ['./agent-evals.component.scss'],
})
export class AgentEvalsComponent implements OnInit, OnChanges {
  @Input() projectDetails: any;

  tests: AgentEvalTest[] = [];
  runs: AgentEvalRun[] = [];
  filter: 'all' | 'failing' | 'critical' | 'never' = 'all';
  search = '';
  loadingTests = false;
  loadingRuns = false;
  deletingId: string | null = null;
  rules: AgentEvalSuccessRules = { ...DEFAULT_RULES };
  private profile = this.appService.getCurrentAccount();

  constructor(
    private agentEvalsService: AgentEvalsService,
    private dialog: MatDialog,
    private notify: NotificationService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectDetails'] && !changes['projectDetails'].firstChange) {
      this.reload();
    }
  }

  get entityId(): string {
    return (
      (this.projectDetails &&
        (this.projectDetails.entity_id || this.projectDetails.entityId)) ||
      (this.profile && this.profile.entity && this.profile.entity.id) ||
      ''
    );
  }

  get projectId(): string {
    return (
      (this.projectDetails &&
        (this.projectDetails.project_id || this.projectDetails.projectId)) ||
      ''
    );
  }

  get projectName(): string {
    return (this.projectDetails && this.projectDetails.project_name) || 'this project';
  }

  get latestCompletedRun(): AgentEvalRun | null {
    const completed = (this.runs || []).filter((r) => r.status === 'completed');
    return completed.length ? completed[0] : null;
  }

  reload(): void {
    if (!this.entityId || !this.projectId) {
      return;
    }
    this.loadRules();
    this.loadTests();
    this.loadRuns();
  }

  /** Map BE snake_case success_rules → FE camelCase. */
  private rulesFromApi(raw: any): AgentEvalSuccessRules {
    const src = raw || {};
    return {
      passMark: Number(src.pass_mark != null ? src.pass_mark : src.passMark) || DEFAULT_RULES.passMark,
      alertBelow:
        Number(src.alert_below != null ? src.alert_below : src.alertBelow) ||
        DEFAULT_RULES.alertBelow,
      criticalDouble:
        src.critical_double != null
          ? !!src.critical_double
          : src.criticalDouble != null
          ? !!src.criticalDouble
          : DEFAULT_RULES.criticalDouble,
      notifyBy: src.notify_by || src.notifyBy || DEFAULT_RULES.notifyBy,
    };
  }

  loadRules(): void {
    this.agentEvalsService.getSettings(this.entityId, this.projectId).subscribe(
      (data) => {
        this.rules = this.rulesFromApi(data && data.success_rules);
      },
      () => {
        this.rules = { ...DEFAULT_RULES };
      }
    );
  }

  saveRules(rules: AgentEvalSuccessRules): void {
    this.agentEvalsService.updateSettings(this.entityId, this.projectId, rules).subscribe(
      (data) => {
        this.rules = this.rulesFromApi(
          (data && data.success_rules) || {
            pass_mark: rules.passMark,
            alert_below: rules.alertBelow,
            critical_double: rules.criticalDouble,
            notify_by: rules.notifyBy,
          }
        );
        this.notify.showMessage('Success rules saved', 2500);
      },
      (err) => {
        const msg =
          (err && err.error && err.error.message) || 'Failed to save success rules';
        this.notify.showMessage(msg, 4000);
      }
    );
  }

  loadTests(): void {
    this.loadingTests = true;
    this.agentEvalsService.listTests(this.entityId, this.projectId).subscribe(
      (tests) => {
        this.tests = tests || [];
        this.loadingTests = false;
      },
      (err) => {
        this.loadingTests = false;
        const msg = (err && err.error && err.error.message) || 'Failed to load tests';
        this.notify.showMessage(msg, 4000);
      }
    );
  }

  loadRuns(): void {
    this.loadingRuns = true;
    this.agentEvalsService.listRuns(this.entityId, this.projectId).subscribe(
      (runs) => {
        this.runs = runs || [];
        this.loadingRuns = false;
      },
      () => {
        this.loadingRuns = false;
      }
    );
  }

  /** Pass threshold for a test given current success rules. */
  passThreshold(test: AgentEvalTest): number {
    if (test && test.critical && this.rules.criticalDouble) {
      return Math.min(100, this.rules.passMark);
    }
    return this.rules.passMark;
  }

  isPassing(test: AgentEvalTest): boolean {
    if (test.last_score == null) {
      return false;
    }
    return Number(test.last_score) >= this.passThreshold(test);
  }

  agentHealth(): number | null {
    const latest = this.latestCompletedRun;
    if (latest && latest.summary && latest.summary.health_score != null) {
      return Math.round(Number(latest.summary.health_score));
    }
    const scored = this.tests.filter((t) => t.last_score != null);
    if (!scored.length) {
      return null;
    }
    let weighted = 0;
    let weightSum = 0;
    scored.forEach((t) => {
      const w = t.critical && this.rules.criticalDouble ? 2 : 1;
      weighted += Number(t.last_score) * w;
      weightSum += w;
    });
    return Math.round(weighted / weightSum);
  }

  healthTone(): string {
    const health = this.agentHealth();
    if (health == null) {
      return 'neutral';
    }
    if (health < this.rules.alertBelow) {
      return 'bad';
    }
    if (health < this.rules.passMark) {
      return 'warn';
    }
    return 'good';
  }

  passingCount(): number {
    return this.tests.filter((t) => this.isPassing(t)).length;
  }

  criticalFailureCount(): number {
    return this.tests.filter(
      (t) => t.critical && t.last_score != null && !this.isPassing(t)
    ).length;
  }

  lastRunLabel(): string {
    const run = this.runs && this.runs.length ? this.runs[0] : null;
    if (!run || !run.started_at) {
      return '—';
    }
    return this.formatRunWhen(run.started_at);
  }

  latestIncorrectOutcomes(): number | null {
    const run = this.latestCompletedRun;
    if (!run || !run.summary || run.summary.incorrect_outcomes_count == null) {
      return null;
    }
    return Number(run.summary.incorrect_outcomes_count);
  }

  private pad2(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }

  formatRunWhen(iso: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return iso;
    }
    // Always render in Asia/Kolkata so run logs match IST regardless of browser TZ.
    let parts: { [key: string]: string } = {};
    try {
      const fmt = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      fmt.formatToParts(d).forEach((p) => {
        if (p.type !== 'literal') {
          parts[p.type] = p.value;
        }
      });
    } catch (e) {
      return iso;
    }
    const y = Number(parts['year']);
    const m = Number(parts['month']);
    const day = Number(parts['day']);
    const hh = parts['hour'] || '00';
    const mm = parts['minute'] || '00';
    const nowParts: { [key: string]: string } = {};
    try {
      new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
        .formatToParts(new Date())
        .forEach((p) => {
          if (p.type !== 'literal') {
            nowParts[p.type] = p.value;
          }
        });
    } catch (e) {
      /* ignore */
    }
    const nowY = Number(nowParts['year']);
    const nowM = Number(nowParts['month']);
    const nowD = Number(nowParts['day']);
    if (y === nowY && m === nowM && day === nowD) {
      return `Today, ${hh}:${mm} IST`;
    }
    const yesterday = new Date(Date.UTC(nowY, nowM - 1, nowD));
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    if (
      y === yesterday.getUTCFullYear() &&
      m === yesterday.getUTCMonth() + 1 &&
      day === yesterday.getUTCDate()
    ) {
      return `Yesterday, ${hh}:${mm} IST`;
    }
    return `${day}/${m}/${y}, ${hh}:${mm} IST`;
  }

  successRulesSummary(): string {
    const notify =
      this.rules.notifyBy && this.rules.notifyBy !== 'None'
        ? ` Notify by ${this.rules.notifyBy}.`
        : '';
    const critical = this.rules.criticalDouble
      ? ' Critical tests count double.'
      : '';
    return (
      `A test passes at ${this.rules.passMark} or more.` +
      critical +
      ` Alert when health drops below ${this.rules.alertBelow}.` +
      notify
    );
  }

  filteredTests(): AgentEvalTest[] {
    const q = (this.search || '').trim().toLowerCase();
    return (this.tests || []).filter((t) => {
      if (this.filter === 'critical' && !t.critical) {
        return false;
      }
      if (this.filter === 'never' && t.last_run_at) {
        return false;
      }
      if (this.filter === 'failing') {
        if (t.last_score == null || this.isPassing(t)) {
          return false;
        }
      }
      if (!q) {
        return true;
      }
      const hay = `${t.name} ${t.area || ''} ${t.description || ''}`.toLowerCase();
      return hay.indexOf(q) >= 0;
    });
  }

  openAddMenu(): void {
    // Prototype "+ Add tests" covers manual + generate
    const ref = this.dialog.open(AddAgentEvalTestDialogComponent, {
      width: '560px',
      data: { entityId: this.entityId, projectId: this.projectId },
    });
    ref.afterClosed().subscribe((created) => {
      if (created) {
        this.loadTests();
      }
    });
  }

  openGenerate(): void {
    const ref = this.dialog.open(GenerateAgentEvalTestsDialogComponent, {
      width: '720px',
      data: { entityId: this.entityId, projectId: this.projectId },
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved && saved.length) {
        this.loadTests();
      }
    });
  }

  openRun(): void {
    const ref = this.dialog.open(RunAgentEvalsDialogComponent, {
      width: '700px',
      maxWidth: '94vw',
      disableClose: true,
      data: {
        entityId: this.entityId,
        projectId: this.projectId,
        tests: this.tests.slice(),
      },
    });
    ref.afterClosed().subscribe(() => {
      this.loadTests();
      this.loadRuns();
    });
  }

  openEditTest(test: AgentEvalTest, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const ref = this.dialog.open(AddAgentEvalTestDialogComponent, {
      width: '560px',
      data: {
        entityId: this.entityId,
        projectId: this.projectId,
        test,
      },
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated) {
        this.loadTests();
      }
    });
  }

  resultForTest(test: AgentEvalTest): AgentEvalTestResult | null {
    const run = this.latestCompletedRun;
    if (!run || !run.results || !test) {
      return null;
    }
    const match = (run.results || []).find(
      (r) => r && (r.test_id === test.test_id || r.name === test.name)
    );
    return match || null;
  }

  openFailedTest(test: AgentEvalTest, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (test.last_score == null || this.isPassing(test)) {
      return;
    }
    const result = this.resultForTest(test);
    if (!result) {
      this.notify.showMessage(
        'Open the report for full detail — this run has no stored result payload yet',
        4000
      );
      return;
    }
    this.dialog.open(AgentEvalTestResultDialogComponent, {
      width: '760px',
      maxWidth: '94vw',
      data: { test, result },
    });
  }

  openReport(run: AgentEvalRun, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!run) {
      return;
    }
    if (run.results && run.results.length) {
      this.dialog.open(AgentEvalReportDialogComponent, {
        width: '960px',
        maxWidth: '96vw',
        maxHeight: '94vh',
        panelClass: 'ae-report-panel',
        data: {
          run,
          projectDetails: this.projectDetails,
          projectName: this.projectName,
        },
      });
      return;
    }
    this.downloadReport(run);
  }

  downloadReport(run: AgentEvalRun, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const url = (run && (run.report_download_url || run.report_url)) || '';
    if (!url) {
      this.notify.showMessage('Report URL not available yet', 3000);
      return;
    }
    const filename = `agent-evals-${run.run_id || 'report'}.html`;
    openPresignedDownload(url, filename);
  }

  downloadInputSheet(run: AgentEvalRun, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const url = (run && run.input_xlsx_url) || '';
    if (!url) {
      this.notify.showMessage('Input sheet not available for this run', 3000);
      return;
    }
    const filename = `agent-evals-input-${run.run_id || 'sheet'}.xlsx`;
    openPresignedDownload(url, filename);
  }

  openSuccessRules(): void {
    const ref = this.dialog.open(AgentEvalSuccessRulesDialogComponent, {
      width: '440px',
      data: { rules: { ...this.rules } },
    });
    ref.afterClosed().subscribe((saved: AgentEvalSuccessRules) => {
      if (saved) {
        this.saveRules(saved);
      }
    });
  }

  deleteTest(test: AgentEvalTest): void {
    if (!confirm(`Delete test "${test.name}"?`)) {
      return;
    }
    this.deletingId = test.test_id;
    this.agentEvalsService.deleteTest(this.entityId, this.projectId, test.test_id).subscribe(
      () => {
        this.deletingId = null;
        this.loadTests();
      },
      (err) => {
        this.deletingId = null;
        const msg = (err && err.error && err.error.message) || 'Delete failed';
        this.notify.showMessage(msg, 4000);
      }
    );
  }

  downloadLatestReport(): void {
    const run = this.latestCompletedRun;
    if (!run) {
      this.notify.showMessage('No report available yet — run Agent Evals first', 3500);
      return;
    }
    this.openReport(run);
  }

  scoreClass(test: AgentEvalTest): string {
    if (test.last_score == null) {
      return 'ae-score-pill--na';
    }
    return this.isPassing(test) ? 'ae-score-pill--pass' : 'ae-score-pill--fail';
  }
}
